import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ASSETS } from '../assetManifest.js';
import { Gamepad2, Settings, LogOut } from 'lucide-react';

interface Props {
  show: boolean;
  onResume: () => void;
  onOpenSettings: () => void;
  onGoToMainMenu: () => void;
}

export function PauseMenu({ show, onResume, onOpenSettings, onGoToMainMenu }: Props) {
  const [resumeButtonHovered, setResumeButtonHovered] = useState(false);
  const [settingsButtonHovered, setSettingsButtonHovered] = useState(false);
  const [mainMenuButtonHovered, setMainMenuButtonHovered] = useState(false);

  // Validate button assets
  const playButtonCfg = (ASSETS as any)?.ui?.play_button;
  const playButtonHoverCfg = (ASSETS as any)?.ui?.play_button_hover;
  const settingsButtonCfg = (ASSETS as any)?.ui?.settings_button;
  const settingsButtonHoverCfg = (ASSETS as any)?.ui?.settings_button_hover;

  if (!playButtonCfg || !playButtonCfg.path || !playButtonHoverCfg || !playButtonHoverCfg.path) {
    throw new Error('Play button assets are missing or invalid in assetManifest.js');
  }
  if (!settingsButtonCfg || !settingsButtonCfg.path || !settingsButtonHoverCfg || !settingsButtonHoverCfg.path) {
    throw new Error('Settings button assets are missing or invalid in assetManifest.js');
  }

  if (!show) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-gradient-to-br from-slate-900/80 via-amber-900/20 to-orange-900/30 z-[2500] flex items-center justify-center p-4"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gradient-to-br from-slate-800/95 via-amber-900/20 to-orange-900/20 backdrop-blur-md rounded-xl shadow-2xl w-full max-w-md border border-amber-500/30 p-8 text-center"
        >
          <h2 className="text-4xl font-bold text-amber-100 mb-8 drop-shadow-lg">Game Paused</h2>
          
          <div className="space-y-4">
            {/* Resume Button */}
            <motion.button
              onClick={onResume}
              onMouseEnter={() => setResumeButtonHovered(true)}
              onMouseLeave={() => setResumeButtonHovered(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Resume Game"
              className="block mx-auto transition-all duration-300 relative flex items-center justify-center bg-gradient-to-r from-emerald-600 to-green-600 hover:from-emerald-500 hover:to-green-500 rounded-xl shadow-lg border border-emerald-400/30"
              style={{
                width: `${playButtonCfg.width || 200}px`,
                height: `${playButtonCfg.height || 60}px`,
              }}
            >
              <span className="text-white font-semibold text-xl drop-shadow flex items-center gap-2">
                <Gamepad2 className="w-6 h-6" />
                Resume
              </span>
            </motion.button>

            {/* Settings Button */}
            <motion.button
              onClick={onOpenSettings}
              onMouseEnter={() => setSettingsButtonHovered(true)}
              onMouseLeave={() => setSettingsButtonHovered(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Settings"
              className="block mx-auto transition-all duration-300 relative flex items-center justify-center bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 rounded-xl shadow-lg border border-blue-400/30"
              style={{
                width: `${settingsButtonCfg.width || 160}px`,
                height: `${settingsButtonCfg.height || 50}px`,
              }}
            >
              <span className="text-white font-medium text-lg drop-shadow flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Settings
              </span>
            </motion.button>

            {/* Return to Main Menu Button */}
            <motion.button
              onClick={onGoToMainMenu}
              onMouseEnter={() => setMainMenuButtonHovered(true)}
              onMouseLeave={() => setMainMenuButtonHovered(false)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Return to Main Menu"
              className="block mx-auto transition-all duration-300 relative flex items-center justify-center bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 rounded-xl shadow-lg border border-red-400/30"
              style={{
                width: `${settingsButtonCfg.width || 160}px`,
                height: `${settingsButtonCfg.height || 50}px`,
              }}
            >
              <span className="text-white font-medium text-lg drop-shadow flex items-center gap-2">
                <LogOut className="w-5 h-5" />
                Main Menu
              </span>
            </motion.button>
          </div>

          <p className="text-sm text-amber-200 mt-8">Press ESC to resume</p>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
